import _range from 'lodash/range';
import { ClickPosition } from '~/utils/click-position';

export interface IVideoEntry {
  element: HTMLVideoElement;
  play: () => void;
  pause: (position: ClickPosition) => void;
}

export class VideoPlayController {
  private _observer: IntersectionObserver = {} as any;
  private _registerMap = new WeakMap<HTMLVideoElement, IVideoEntry>();
  private _playedMap = new WeakMap<HTMLVideoElement, boolean>();
  private _videoToPlay: IVideoEntry | null = null;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    this._observer = new IntersectionObserver(
      (entries, observer) => {
        let videoToPlay: HTMLVideoElement | null = null as any;
        let videoToPlayTop: number = Number.MIN_SAFE_INTEGER;

        const videosToStop: HTMLVideoElement[] = [];

        entries.forEach((entry) => {
          const { intersectionRatio, isIntersecting, boundingClientRect } = entry;
          const video = entry.target as HTMLVideoElement;

          /**
           * select the top one among the videos that can be played.
           */
          if (isIntersecting && intersectionRatio > 0.8 && !this._playedMap.get(video)) {
            if (boundingClientRect.top > videoToPlayTop) {
              videoToPlay = video;
              videoToPlayTop = boundingClientRect.top;
            }
          }

          /**
           * find video out of view
           */
          if (!isIntersecting || intersectionRatio < 0.5) {
            videosToStop.push(video);
          }
        });

        /**
         * stop all out-of-view videos
         */
        videosToStop.forEach((video) => {
          const entry = this._registerMap.get(video);
          if (entry?.element) {
            const video = entry.element;
            if (video.currentTime && video.currentTime !== video.duration && !video.paused) {
              entry.pause(ClickPosition.main_video_pause_auto);
            }
          }
          if (this._videoToPlay?.element === entry?.element) {
            this._videoToPlay = null;
          }
        });

        /**
         * play first in-view video
         */
        if (videoToPlay) {
          this._videoToPlay = this._registerMap.get(videoToPlay) || null;
          const entry = this._registerMap.get(videoToPlay);
          if (entry) {
            this._playedMap.set(entry.element, true);
            entry.play();
          }
        }
      },
      {
        threshold: _range(0, 1.1, 0.1),
      },
    );

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (this._videoToPlay?.element) {
          const video = this._videoToPlay.element;
          if (video.currentTime && video.currentTime !== video.duration && !video.paused) {
            this._videoToPlay.pause(ClickPosition.main_video_pause_auto);
          }
        }
      } else {
        this._videoToPlay?.play();
      }
    });
  }

  public register(videoEntry: IVideoEntry): void {
    const { element } = videoEntry;
    this._registerMap.set(element, videoEntry);
    this._observer.observe(element);

    // trigger first intersection check
    const rect = element.getBoundingClientRect();
    const middle = (rect.bottom + rect.top) / 2;

    if (middle < window.innerHeight) {
      videoEntry.play();
      this._videoToPlay = videoEntry;
      this._playedMap.set(element, true);
    }
  }

  public unregister(video: IVideoEntry): void {
    this._registerMap.delete(video.element);
    this._observer.unobserve(video.element);
  }
}

export const videoPlayController = new VideoPlayController();
